import Swal from 'sweetalert2';
import { useNavigate } from "react-router-dom";
import { grupoMostrarGruposPorUsuario, usuarioDelete, usuarioMostrar, usuarioReiniciarContraseña } from "../../api/usuarios.api";
import { validarAutenticacion } from "../../../auth";
import { CONST_CODIGO_USUARIOS_AGREGAR, CONST_CODIGO_USUARIOS_EDITAR, CONST_CODIGO_USUARIOS_GRUPOS, CONST_CODIGO_USUARIOS_RESETEAR_CONTRASEÑA, CONST_RUTA_SIN_ACCESO, CONST_RUTA_USUARIOS_AGREGAR, CONST_RUTA_USUARIOS_EDITAR, CONST_RUTA_USUARIOS_GRUPOS } from "../../../../app/constants/constants-rutas-codigos";

// Acciones del listado de usuarios: reiniciar contraseña, agregar, grupos,
// editar, eliminar y re-consulta con los filtros actuales.
export const useUsuariosAcciones = ({
  usuarioAutenticado,
  watch,
  queryClient,
  setFiltrosAplicados,
}: any) => {

  const resetPassword = async (UseCod) => {
    const validarAutenticado = await validarAutenticacion(usuarioAutenticado?.token, CONST_CODIGO_USUARIOS_RESETEAR_CONTRASEÑA)
    if (!validarAutenticado) {
      navigate(CONST_RUTA_SIN_ACCESO)
      return
    }

    // QA "Restablecer contraseña" 2026-08-14: el backend ya NO fija la contraseña = usuario.
    // Genera una contraseña COMPLEJA aleatoria y la ENVÍA por correo al usuario (nunca vuelve
    // al navegador). La confirmación refleja ese comportamiento.
    const confirmar = await Swal.fire({
      title: '¿Reiniciar contraseña?',
      html: `Se reiniciará la contraseña de <b>${UseCod}</b>.<br/>Se generará una contraseña segura y se enviará al <b>correo registrado</b> del usuario.`,
      icon: 'warning' as const,
      showCancelButton: true,
      confirmButtonColor: '#6366f1',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Reiniciar',
      cancelButtonText: 'Cancelar',
    })
    if (!confirmar.isConfirmed) return

    // El backend (c_usuario.reiniciar_contrasena) genera la clave compleja, la envía por
    // correo y SOLO la persiste si el correo llegó (status 1). Si el canal no está
    // configurado, no hay correo o el envío falla, responde status 0 con el motivo.
    const dataPOST = {
      usuario: UseCod,
      token: usuarioAutenticado?.token
    }
    const res = await usuarioReiniciarContraseña(dataPOST)

    if (res?.status === 1) {
      // La contraseña NO se muestra (viaja por correo, no al navegador). Se muestra el
      // mensaje del backend ("Se envió la nueva contraseña al correo del usuario.").
      Swal.fire({
        title: 'Contraseña reiniciada',
        html: res?.message || `Se envió la nueva contraseña al correo de <b>${UseCod}</b>.`,
        icon: 'success' as const,
      })
    } else {
      // status 0: el backend indica el motivo (canal no configurado / sin correo / envío
      // falló). La contraseña actual del usuario NO se modificó.
      Swal.fire('No se pudo reiniciar la contraseña', res?.message || '', 'warning')
    }
  }


  const navigate = useNavigate()

  const usuarioAgregar = async () => {

    const validarAutenticado = await validarAutenticacion(usuarioAutenticado?.token, CONST_CODIGO_USUARIOS_AGREGAR)
    if (validarAutenticado) {
      navigate(CONST_RUTA_USUARIOS_AGREGAR,{state:{compania:watch("compania"),sucursal1:watch("sucursal"),sede1:watch("Sede")}} )
    } else {
      navigate(CONST_RUTA_SIN_ACCESO)
    }
  }

  const usuarioGrupo = async (compania, nombreUsuario, codUsuario) => {

    const validarAutenticado = await validarAutenticacion(usuarioAutenticado?.token, CONST_CODIGO_USUARIOS_GRUPOS)

    if (validarAutenticado) {
      const dataPOST = {
        codigo_usuario: codUsuario,
        compania: compania,
        token: usuarioAutenticado?.token
      }

      const { grupos } = await grupoMostrarGruposPorUsuario(dataPOST)

      navigate(CONST_RUTA_USUARIOS_GRUPOS, {
        state: {
          grupos: grupos,
          nombreUsuario: nombreUsuario,
          codUsuario: codUsuario,
          compania: compania
        }
      })
    } else {
      navigate(CONST_RUTA_SIN_ACCESO)
    }

  }

  const usuarioEditar = async (UseCod) => {

    const validarAutenticado = await validarAutenticacion(usuarioAutenticado?.token, CONST_CODIGO_USUARIOS_EDITAR)

    if (validarAutenticado) {

      const dataPOST = {
        usuario: UseCod,
        token: usuarioAutenticado?.token
      }
      const { usuario } = await usuarioMostrar(dataPOST)

      navigate(CONST_RUTA_USUARIOS_EDITAR, {
        state: {usuario:usuario, compania:watch("compania"),sucursal1:watch("sucursal"),sede1:watch("Sede")}
      })
    } else {
      navigate(CONST_RUTA_SIN_ACCESO)
    }
  }

  const optionsEliminar = (nombreCompleto) => {
    return {
      title: `¿Estás seguro de eliminar al usuario: ${nombreCompleto}?`,
      icon: 'warning' as const,
      showCancelButton: true,
      cancelButtonColor: '#6c757d',
      confirmButtonColor: '#d33',
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
    }
  }

  const usuarioEliminar = (usuario, nombreCompleto) => {
    Swal.fire(optionsEliminar(nombreCompleto)).then(async (result) => {
      if (result.isConfirmed) {

        const dataPOST = {
          usuario: usuario,
          token: usuarioAutenticado?.token
        }

        const { status, message } = await usuarioDelete(dataPOST)
        if (status === 1) {
          obtenerUsuarios()
          Swal.fire(
            'Usuario eliminado',
            '',
            'success'
          )
        } else {
          // REF-ERRORES-ESPECIFICOS (2026-07-14): icono corregido ('success'→
          // 'error') + se muestra el motivo del backend.
          Swal.fire(
            'Error al eliminar usuario',
            message || '',
            'error'
          )
        }
      }
    })
  }

  // Aplica los filtros actuales (watch) y fuerza re-fetch fresco. Lo usan la carga inicial,
  // el post-eliminar y el cierre de modal (invalidate = re-consulta aunque la clave no cambie).
  const obtenerUsuarios = () => {
    setFiltrosAplicados({
      // UseCod: watch("UseCod") || "",
      NombreCompleto: "",
      Compania: watch("compania"),
      Sucursal: watch("sucursal"),
      Estado: watch("Estado") || "A",
      Sede: watch("Sede"),
    })
    queryClient.invalidateQueries({ queryKey: ["seg-usuarios"] })
  }

  return {
    resetPassword,
    usuarioAgregar,
    usuarioGrupo,
    usuarioEditar,
    usuarioEliminar,
    obtenerUsuarios,
  }
}
